import React, { useState, useEffect } from 'react';
import { doc, getDoc, collectionGroup, query, where, onSnapshot } from 'firebase/firestore';
import { useFirebase } from '../App';

const TeacherDashboardPage = ({ navigate }) => {
    const { user, db, isAuthReady } = useFirebase();
    const [delegates, setDelegates] = useState([]);
    const [teacherCode, setTeacherCode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // 1. Safety Checks
        if (!isAuthReady) return;
        if (!user) {
            navigate('login');
            return;
        }

        setLoading(true);

        // 2. Fetch Teacher's Own Code (from their user profile)
        const fetchTeacherProfile = async () => {
            try {
                const userDocRef = doc(db, 'users', user.uid);
                const docSnap = await getDoc(userDocRef);
                if (docSnap.exists()) {
                    setTeacherCode(docSnap.data().teacherCode || 'Error: No Code Found');
                }
            } catch (err) {
                console.error("Error fetching teacher profile:", err);
            }
        };

        fetchTeacherProfile();

        // 3. Subscribe to Delegates (Collection Group Query)
        // This searches ALL 'thessmunDelegateInfo' collections across the entire database
        // for documents where 'teacherUid' matches the current user.
        try {
            const delegatesRef = collectionGroup(db, 'thessmunDelegateInfo');
            const q = query(delegatesRef, where('teacherUid', '==', user.uid));

            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const fetchedDelegates = [];
                querySnapshot.forEach((doc) => {
                    fetchedDelegates.push({ id: doc.id, ...doc.data() });
                });
                setDelegates(fetchedDelegates);
                setLoading(false);
            }, (err) => {
                console.error("Error fetching delegates:", err);
                // Specific helpful error for the missing index issue
                if (err.code === 'failed-precondition') {
                    setError("Missing Database Index. Open your Browser Console (F12) and click the link from Firebase to create the index.");
                } else {
                    setError("Failed to load delegates. " + err.message);
                }
                setLoading(false);
            });

            return () => unsubscribe();
        } catch (err) {
            console.error("Setup error:", err);
            setError(err.message);
            setLoading(false);
        }

    }, [user, db, isAuthReady, navigate]);

    // --- Helper for Status Colors ---
    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved': return 'bg-green-100 text-green-800 border-green-200';
            case 'Rejected': return 'bg-red-100 text-red-800 border-red-200';
            case 'Waitlisted': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200'; // Pending
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-xl font-semibold animate-pulse">Loading Dashboard...</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-6 py-12 animate-fade-in">
            {/* Header Section */}
            <div className="bg-white rounded-lg shadow-lg p-8 mb-8 border border-gray-200" 
                 style={{ backgroundColor: 'var(--color-section1-card-bg)', color: 'var(--color-section1-card-text)' }}>
                <div className="flex flex-col md:flex-row justify-between items-center">
                    <div>
                        <h2 className="text-3xl font-bold mb-2">Teacher Dashboard</h2>
                        <p className="opacity-75">Welcome back, {user?.fullName || 'Advisor'}</p>
                    </div>
                    <div className="mt-4 md:mt-0 text-center bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <p className="text-sm font-bold text-blue-800 uppercase tracking-wide">Your Group Code</p>
                        <p className="text-3xl font-mono font-bold text-blue-600 select-all cursor-pointer" 
                           onClick={() => navigator.clipboard.writeText(teacherCode)}>
                            {teacherCode || "..."}
                        </p>
                        <p className="text-xs text-blue-500 mt-1">Share this with your delegates</p>
                    </div>
                </div>
            </div>

            {/* Error Message Display */}
            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-8" role="alert">
                    <p className="font-bold">Error Loading Data</p>
                    <p>{error}</p>
                </div>
            )}

            {/* Delegates List */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200"
                 style={{ backgroundColor: 'var(--color-section1-card-bg)' }}>
                <div className="p-6 border-b border-gray-200 bg-gray-50 flex justify-between items-center"
                     style={{ backgroundColor: 'var(--color-section1-icon-bg)' }}>
                    <h3 className="text-xl font-bold">Registered Delegates ({delegates.length})</h3>
                </div>

                {delegates.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        <p className="text-lg">No delegates have registered with your code yet.</p>
                        <p className="mt-2 text-sm">Once they register using code <strong>{teacherCode}</strong>, they will appear here.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="text-sm font-semibold uppercase tracking-wider text-gray-500 border-b bg-gray-50"
                                    style={{ backgroundColor: 'var(--color-section1-icon-bg)' }}>
                                    <th className="px-6 py-4">Name</th>
                                    <th className="px-6 py-4">Email</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Committee</th>
                                    <th className="px-6 py-4">Country</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {delegates.map((delegate) => (
                                    <tr key={delegate.id} className="hover:bg-gray-50 transition-colors"
                                        style={{ backgroundColor: 'var(--color-section1-card-bg)', color: 'var(--color-body-text)' }}>
                                        <td className="px-6 py-4 font-medium">{delegate.fullName}</td>
                                        <td className="px-6 py-4 text-sm opacity-75">{delegate.email}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(delegate.registrationStatus)}`}>
                                                {delegate.registrationStatus || 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm">{delegate.committeeAllocation || '-'}</td>
                                        <td className="px-6 py-4 text-sm">{delegate.countryAllocation || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeacherDashboardPage; // Export default for easier importing